import { useRef, useState, useCallback } from 'react';

type AudioRecorderReturn = {
  audioUrl: string | null;
  audioBlob: Blob | null;
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetRecorder: () => void;
  transcribedText: string | null;
  isTranscribing: boolean;
  transcriptionError: string | null;
  audioLevel: number; // For visualizing audio input level
};

export const useAudioRecorder = (): AudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);

  const mediaStream = useRef<MediaStream | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const animationId = useRef<number | null>(null);
  const smoothLevel = useRef<number>(0);
  const running = useRef<boolean>(false);
  const dataArray = useRef<Uint8Array | null>(null);
  const transcriptionRun = useRef<number>(0);

  const uploadAudioToCloudinary = async (blob: Blob): Promise<string> => {
    // For now, we'll use a base64 data URL as a fallback
    // In production, you should:
    // 1. Set up a Cloudinary account
    // 2. Create an unsigned upload preset
    // 3. Replace CLOUD_NAME and UPLOAD_PRESET below

    const CLOUD_NAME = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME || 'YOUR_CLOUD_NAME';
    const UPLOAD_PRESET = process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET || 'ml_default';

    // If Cloudinary is not configured, convert to base64 data URL
    if (CLOUD_NAME === 'YOUR_CLOUD_NAME') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to convert audio to base64'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');
    formData.append('upload_preset', UPLOAD_PRESET);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/video/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload audio to Cloudinary');
      }

      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      // Fallback to creating a base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to convert audio to base64'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
  };

  const transcribeAudio = async (audioBlob: Blob): Promise<string> => {
    const runId = ++transcriptionRun.current;
    try {
      setIsTranscribing(true);
      setTranscriptionError(null);

      // Create FormData with the audio blob
      const formData = new FormData();
      
      // Determine file extension based on blob type (same as pop-island.tsx)
      const extension = (() => {
        const type = (audioBlob?.type || "").toLowerCase();
        if (type.includes("mp4")) return "mp4";
        if (type.includes("ogg")) return "ogg";
        if (type.includes("mpeg")) return "mp3";
        return "webm";
      })();
      
      // Use 'audio' field name to match transcribe-groq API (same as pop-island.tsx)
      formData.append("audio", audioBlob, `recording.${extension}`);

      // Use transcribe-groq API (same as pop-island.tsx)
      const response = await fetch('/api/transcribe-groq', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Handle the response from transcribe-groq API (returns { text: string })
      let transcribedText = '';

      if (data && data.text) {
        transcribedText = data.text;
      } else {
        throw new Error('Transcription response did not contain text');
      }

      const trimmed = transcribedText.trim();
      if (transcriptionRun.current === runId) {
        setTranscribedText(trimmed);
      }
      return trimmed;
    } catch (error) {
      console.error('Transcription error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Transcription failed';
      if (transcriptionRun.current === runId) {
        setTranscriptionError(errorMessage);
      }
      throw error;
    } finally {
      if (transcriptionRun.current === runId) {
        setIsTranscribing(false);
      }
    }
  };

  // Audio analysis helper functions
  const MIN_DB = -70;
  const MAX_DB = -10;
  const ATTACK = 0.25;
  const RELEASE = 0.05;

  const dbToNorm = (db: number) => {
    if (db <= MIN_DB) return 0;
    if (db >= MAX_DB) return 1;
    return (db - MIN_DB) / (MAX_DB - MIN_DB);
  };

  const rmsFromTimeDomain = (buf: Uint8Array) => {
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  };

  const rmsToDb = (rms: number) => {
    const minRms = 1e-8;
    return 20 * Math.log10(Math.max(rms, minRms));
  };

  const updateAudioLevel = useCallback(() => {
    if (!running.current || !analyser.current || !dataArray.current) {
      return;
    }

    analyser.current.getByteTimeDomainData(dataArray.current);
    const rms = rmsFromTimeDomain(dataArray.current);
    const db = rmsToDb(rms);
    const norm = dbToNorm(db);

    const alpha = norm > smoothLevel.current ? ATTACK : RELEASE;
    smoothLevel.current = alpha * norm + (1 - alpha) * smoothLevel.current;

    setAudioLevel(smoothLevel.current);

    // Continue the animation loop
    animationId.current = requestAnimationFrame(updateAudioLevel);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      // Reset previous recording data
      setAudioUrl(null);
      setAudioBlob(null);
      setTranscribedText(null);
      setTranscriptionError(null);
      setAudioLevel(0);
      smoothLevel.current = 0;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream.current = stream;

      // Set up audio analysis
      audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.current.createMediaStreamSource(stream);
      analyser.current = audioContext.current.createAnalyser();
      analyser.current.fftSize = 2048;
      source.connect(analyser.current);

      // Create data array for audio analysis
      dataArray.current = new Uint8Array(analyser.current.fftSize);

      const preferredMimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/mpeg',
      ];

      const supportedMimeType = preferredMimeTypes.find((type) => {
        if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
          return false;
        }
        return MediaRecorder.isTypeSupported(type);
      });

      const recorderOptions = supportedMimeType
        ? ({ mimeType: supportedMimeType } as MediaRecorderOptions)
        : undefined;

      const setupRecorder = (options?: MediaRecorderOptions) => {
        const recorder = options ? new MediaRecorder(stream, options) : new MediaRecorder(stream);
        chunks.current = [];
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.current.push(event.data);
          }
        };
        recorder.onstop = async () => {
          const blobType = options?.mimeType || supportedMimeType || 'audio/webm';
          const audioBlob = new Blob(chunks.current, { type: blobType });
          setAudioBlob(audioBlob);

          const localUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(localUrl);

          try {
            await transcribeAudio(audioBlob);
          } catch (error) {
            console.error('Auto-transcription failed:', error);
          }

          chunks.current = [];
        };
        return recorder;
      };

      try {
        mediaRecorder.current = setupRecorder(recorderOptions);
        mediaRecorder.current.start();
      } catch (recorderError) {
        console.warn('MediaRecorder start failed with preferred options, retrying without options:', recorderError);
        mediaRecorder.current = setupRecorder(undefined);
        mediaRecorder.current.start();
      }
      setIsRecording(true);

      // Start audio level monitoring
      running.current = true;
      updateAudioLevel();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }, [updateAudioLevel]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }

    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStream.current = null;
    }

    // Clean up audio analysis
    running.current = false;
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }

    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close();
    }
    audioContext.current = null;
    analyser.current = null;
    dataArray.current = null;
    setAudioLevel(0);
    smoothLevel.current = 0;
  }, []);

  const resetRecorder = useCallback(() => {
    transcriptionRun.current += 1;
    running.current = false;
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }

    if (mediaRecorder.current) {
      if (mediaRecorder.current.state === 'recording') {
        mediaRecorder.current.onstop = null;
        mediaRecorder.current.stop();
      }
      mediaRecorder.current = null;
    }

    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
      mediaStream.current = null;
    }

    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close();
    }
    audioContext.current = null;
    analyser.current = null;
    dataArray.current = null;

    chunks.current = [];
    setIsRecording(false);
    setIsTranscribing(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscribedText(null);
    setTranscriptionError(null);
    setAudioLevel(0);
    smoothLevel.current = 0;
  }, []);

  return {
    audioUrl,
    audioBlob,
    isRecording,
    startRecording,
    stopRecording,
    resetRecorder,
    transcribedText,
    isTranscribing,
    transcriptionError,
    audioLevel,
  };
};
